import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Check, Mail, Phone, Clock, Inbox, Trash2, UserPlus } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useIntakes } from "@/intakes/IntakesContext";
import { Button } from "@/components/ui-kit/Button";

const FILTERS = [
  { key: "new", label: "New" },
  { key: "handled", label: "Handled" },
  { key: "", label: "All" },
];

function fmt(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function IntakeRow({ intake, onChanged }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const handled = intake.status === "handled";

  async function setStatus(status) {
    setBusy(true);
    try {
      await api.patch(`/intakes/${intake.id}`, { status });
      onChanged();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!window.confirm("Delete this intake?")) return;
    setBusy(true);
    try {
      await api.delete(`/intakes/${intake.id}`);
      onChanged();
    } catch (e) {
      alert(formatApiErrorDetail(e.response?.data?.detail));
      setBusy(false);
    }
  }

  function startNewClient() {
    navigate("/clients-matters", {
      state: {
        prefillClient: {
          name: intake.name,
          email: intake.personal_email,
          phone: intake.phone,
          intakeId: intake.id,
        },
      },
    });
  }

  return (
    <div
      className="flex flex-col gap-3 border-b border-border px-4 py-3.5 last:border-b-0 sm:flex-row sm:items-center sm:justify-between"
      data-testid={`intake-row-${intake.id}`}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-foreground" data-testid={`intake-name-${intake.id}`}>
            {intake.name}
          </span>
          {handled ? (
            <span className="rounded-full bg-status-paid/10 px-2 py-0.5 text-[11px] font-medium text-status-paid">
              Handled
            </span>
          ) : (
            <span className="rounded-full bg-amber-500/10 px-2 py-0.5 text-[11px] font-medium text-amber-600">
              New
            </span>
          )}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
          <a href={`mailto:${intake.personal_email}`} className="inline-flex items-center gap-1 hover:text-foreground">
            <Mail className="h-3.5 w-3.5" /> {intake.personal_email}
          </a>
          <a href={`tel:${intake.phone}`} className="inline-flex items-center gap-1 hover:text-foreground">
            <Phone className="h-3.5 w-3.5" /> {intake.phone}
          </a>
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3.5 w-3.5" /> {fmt(intake.created_at)}
          </span>
        </div>
        {intake.notes && <p className="mt-1.5 text-xs text-muted-foreground">{intake.notes}</p>}
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        <Button
          variant="secondary"
          size="sm"
          disabled={busy}
          onClick={startNewClient}
          data-testid={`intake-start-client-${intake.id}`}
        >
          <UserPlus className="h-4 w-4" /> Start new client
        </Button>
        {handled ? (
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={() => setStatus("new")}
            data-testid={`intake-reopen-${intake.id}`}
          >
            Reopen
          </Button>
        ) : (
          <Button
            size="sm"
            disabled={busy}
            onClick={() => setStatus("handled")}
            data-testid={`intake-mark-handled-${intake.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Mark handled
          </Button>
        )}
        <button
          type="button"
          disabled={busy}
          onClick={remove}
          data-testid={`intake-delete-${intake.id}`}
          title="Delete intake"
          className="rounded-md p-2 text-muted-foreground transition hover:bg-destructive/10 hover:text-destructive disabled:opacity-40"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

export function IntakesManager() {
  const [filter, setFilter] = useState("new");
  const { refresh } = useIntakes();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState("");

  async function load() {
    setError("");
    try {
      const res = await api.get("/intakes", { params: filter ? { status: filter } : {} });
      setRows(res.data);
      refresh();
    } catch (e) {
      setRows([]);
      setError(formatApiErrorDetail(e.response?.data?.detail) || "Could not load intakes.");
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  return (
    <div className="space-y-4" data-testid="intakes-manager">
      <div className="flex items-center gap-2">
        {FILTERS.map((f) => (
          <button
            key={f.key || "all"}
            onClick={() => setFilter(f.key)}
            data-testid={`intakes-filter-${f.key || "all"}`}
            className={`rounded-md px-3 py-1.5 text-sm font-medium transition ${
              filter === f.key
                ? "bg-primary text-primary-foreground"
                : "bg-secondary text-muted-foreground hover:text-foreground"
            }`}
          >
            {f.label}
          </button>
        ))}
        <Button variant="ghost" size="sm" onClick={load} className="ml-auto" data-testid="intakes-refresh">
          Refresh
        </Button>
      </div>

      {error && <p className="text-sm text-destructive" data-testid="intakes-error">{error}</p>}

      {rows === null ? (
        <div className="grid place-items-center py-6">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      ) : rows.length === 0 ? (
        <div className="grid place-items-center gap-2 py-10 text-center text-muted-foreground" data-testid="intakes-empty">
          <Inbox className="h-8 w-8 opacity-50" />
          <p className="text-sm">No {filter === "new" ? "new " : filter === "handled" ? "handled " : ""}intakes yet.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          {rows.map((r) => (
            <IntakeRow key={r.id} intake={r} onChanged={load} />
          ))}
        </div>
      )}
    </div>
  );
}
